import React, { useState, useEffect, useCallback } from 'react';
import SelectBuscable from '../components/SelectBuscable';
import { useCompany } from '../CompanyContext';
import { useImpersonation } from '../ImpersonationContext';
import {
  JobPositionLegalFields,
  fetchJobPositionsFull,
  fetchBiologicalAgents,
  fetchBiologicalExposures,
  createBiologicalExposure,
  deleteBiologicalExposure,
  fetchBiologicalHygiene,
  saveBiologicalHygiene,
  JobPositionBiologicalExposureRow,
} from '../lib/gestion-tecnica-constructor';
import {
  BiologicalAgent,
  HygieneAnswers,
  HygieneResponse,
  HYGIENE_ITEMS_CATALOG,
  calcularPorcentajeMH,
  calcularRiesgoBiologico,
  ViaProbabilidad,
  NAB,
  LEB,
} from '../lib/biogaval';

const NIVEL_COLOR: Record<string, string> = {
  aceptable: '#16a34a',
  mejorable: '#ca8a04',
  intolerable: '#dc2626',
};

const inputStyle: React.CSSProperties = {
  width: '100%', background: '#0f172a', border: '1px solid #334155',
  borderRadius: 6, color: '#e2e8f0', padding: '8px 10px', marginTop: 4,
};

export default function BiogavalMethodologyPage() {
  const { company } = useCompany();
  const { impersonated } = useImpersonation();
  const companyId = (impersonated ?? company)?.id ?? '';

  const [jobPositions, setJobPositions] = useState<JobPositionLegalFields[]>([]);
  const [selectedJobPositionId, setSelectedJobPositionId] = useState('');
  const [agents, setAgents] = useState<BiologicalAgent[]>([]);
  const [hygieneAnswers, setHygieneAnswers] = useState<HygieneAnswers>({});
  const [exposures, setExposures] = useState<JobPositionBiologicalExposureRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const [agentId, setAgentId] = useState('');
  const [viaProbabilidad, setViaProbabilidad] = useState<ViaProbabilidad>('incidencia');
  const [valorProbabilidad, setValorProbabilidad] = useState('');
  const [frecuenciaPct, setFrecuenciaPct] = useState('');
  const [vacunacionPct, setVacunacionPct] = useState('');

  const loadPositionsAndAgents = useCallback(async () => {
    if (!companyId) return;
    setLoading(true);
    try {
      const [positions, agentsList] = await Promise.all([
        fetchJobPositionsFull(companyId),
        fetchBiologicalAgents(),
      ]);
      setJobPositions(positions);
      setAgents(agentsList);
    } catch (e: any) {
      setError(e.message ?? 'Error al cargar datos');
    } finally {
      setLoading(false);
    }
  }, [companyId]);

  const loadPositionData = useCallback(async (jobPositionId: string) => {
    if (!jobPositionId) {
      setHygieneAnswers({});
      setExposures([]);
      return;
    }
    const [hygiene, exp] = await Promise.all([
      fetchBiologicalHygiene(jobPositionId),
      fetchBiologicalExposures(jobPositionId),
    ]);
    setHygieneAnswers(hygiene);
    setExposures(exp);
  }, []);

  useEffect(() => { loadPositionsAndAgents(); }, [loadPositionsAndAgents]);
  useEffect(() => { loadPositionData(selectedJobPositionId); }, [selectedJobPositionId, loadPositionData]);

  const porcentajeMH = calcularPorcentajeMH(hygieneAnswers);

  const handleHygieneChange = async (itemId: string, respuesta: HygieneResponse) => {
    const next = { ...hygieneAnswers, [itemId]: respuesta };
    setHygieneAnswers(next);
    if (!companyId || !selectedJobPositionId) return;
    try {
      await saveBiologicalHygiene(companyId, selectedJobPositionId, next);
    } catch (e: any) {
      setError(e.message ?? 'Error al guardar higiene');
    }
  };

  const handleAddExposure = async () => {
    setError('');
    if (!companyId || !selectedJobPositionId) { setError('Selecciona un puesto de trabajo'); return; }
    if (!agentId) { setError('Selecciona un agente biológico'); return; }
    if (valorProbabilidad === '' || frecuenciaPct === '' || vacunacionPct === '') {
      setError('Completa todos los campos'); return;
    }
    setSaving(true);
    try {
      await createBiologicalExposure({
        companyId,
        jobPositionId: selectedJobPositionId,
        exposure: {
          agent_id: agentId,
          via_probabilidad: viaProbabilidad,
          valor_probabilidad: Number(valorProbabilidad),
          frecuencia_pct: Number(frecuenciaPct),
          vacunacion_pct: Number(vacunacionPct),
        },
      });
      setAgentId(''); setValorProbabilidad(''); setFrecuenciaPct(''); setVacunacionPct('');
      await loadPositionData(selectedJobPositionId);
    } catch (e: any) {
      setError(e.message ?? 'Error al guardar evaluación');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteExposure = async (id: string) => {
    if (!window.confirm('¿Eliminar esta evaluación?')) return;
    await deleteBiologicalExposure(id);
    await loadPositionData(selectedJobPositionId);
  };

  const resultados = exposures.map(exp => {
    const agent: BiologicalAgent = {
      id: exp.agent_id,
      name: exp.agent_name,
      grupo: exp.agent_grupo,
      vias_transmision: exp.agent_vias as any,
    };
    const r = calcularRiesgoBiologico(agent, {
      agent_id: exp.agent_id,
      via_probabilidad: exp.via_probabilidad,
      valor_probabilidad: exp.valor_probabilidad,
      frecuencia_pct: exp.frecuencia_pct,
      vacunacion_pct: exp.vacunacion_pct,
    }, porcentajeMH);
    return { exposureId: exp.id, ...r };
  });

  if (loading) return <div style={{ padding: 24, color: '#94a3b8' }}>Cargando…</div>;

  return (
    <div style={{ padding: 24, maxWidth: 900 }}>
      <h1 style={{ fontSize: 22, marginBottom: 6, color: '#e2e8f0' }}>Metodología BIOGAVAL</h1>
      <p style={{ fontSize: 13, color: '#94a3b8', marginBottom: 20 }}>
        Evaluación cuantitativa del riesgo biológico (BIOGAVAL-NEO 2018, INVASSAT).
        Selecciona un puesto de trabajo, completa el cuestionario de medidas higiénicas
        y evalúa los agentes biológicos aplicables. R = G+T+P+F-V-MH · NAB = {NAB} · LEB = {LEB}.
      </p>

      {jobPositions.length === 0 && (
        <div style={{ background: '#78350f', color: '#fde68a', padding: '10px 14px', borderRadius: 8, marginBottom: 14, fontSize: 13 }}>
          ⚠️  Todavía no tienes puestos de trabajo creados. Crea uno primero en <strong>Puestos de Trabajo</strong> para poder evaluarlos en Metodología BIOGAVAL.
        </div>
      )}

      <div style={{ marginBottom: 20 }}>
        <label style={{ display: 'block', marginBottom: 4, color: '#e2e8f0', fontWeight: 600 }}>
          1. Puesto de trabajo
        </label>
        <SelectBuscable inputStyle={{ ...inputStyle, paddingRight: 32 }} ariaLabel="Puesto de trabajo" value={selectedJobPositionId}
          onChange={setSelectedJobPositionId} vacio="— Seleccionar —" opciones={jobPositions.map(p => ({ value: p.id, label: p.name }))} />
      </div>

      {selectedJobPositionId && (
        <>
          <div style={{ marginBottom: 20, background: '#0f172a', border: '1px solid #334155', borderRadius: 10, padding: 12 }}>
            <label style={{ fontWeight: 600, display: 'block', marginBottom: 4, color: '#e2e8f0' }}>
              2. Medidas higiénicas del puesto ({Math.round(porcentajeMH)}% completado)
            </label>
            <p style={{ fontSize: 12, color: '#94a3b8', marginBottom: 10 }}>
              Aplica a todos los agentes evaluados en este puesto.
            </p>
            <div style={{ maxHeight: 260, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 6 }}>
              {HYGIENE_ITEMS_CATALOG.map(item => (
                <div key={item.id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, fontSize: 12, color: '#cbd5e1' }}>
                  <span style={{ flex: 1, minWidth: 0 }}>{item.label}</span>
                  <select
                    style={{ ...inputStyle, width: 120, flexShrink: 0 }}
                    value={hygieneAnswers[item.id] ?? ''}
                    onChange={e => handleHygieneChange(item.id, e.target.value as HygieneResponse)}
                  >
                    <option value="" style={{ color: '#000000' }}>—</option>
                    <option value="si" style={{ color: '#000000' }}>Sí</option>
                    <option value="no" style={{ color: '#000000' }}>No</option>
                    <option value="no_aplicable" style={{ color: '#000000' }}>N/A</option>
                  </select>
                </div>
              ))}
            </div>
          </div>

          <div style={{ marginBottom: 12 }}>
            <label style={{ fontWeight: 600, display: 'block', marginBottom: 8, color: '#e2e8f0' }}>
              3. Agentes biológicos evaluados
            </label>
            {resultados.length === 0 && (
              <p style={{ fontSize: 13, color: '#94a3b8', marginBottom: 10 }}>Sin evaluaciones todavía.</p>
            )}
            {resultados.map(r => (
              <div key={r.exposureId} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: '#0f172a', border: '1px solid #334155', borderRadius: 10, padding: '10px 14px', marginBottom: 8 }}>
                <div>
                  <strong style={{ fontSize: 14, color: '#e2e8f0' }}>{r.agent_name}</strong>
                  <div style={{ fontSize: 12, color: '#94a3b8' }}>
                    G={r.G} T={r.T} P={r.P} F={r.F} V={r.V} MH={r.MH} · R={r.R}
                  </div>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <span style={{ padding: '4px 10px', borderRadius: 6, fontSize: 12, fontWeight: 600, background: NIVEL_COLOR[r.nivel] + '22', color: NIVEL_COLOR[r.nivel] }}>
                    {r.nivel}
                  </span>
                  <button onClick={() => handleDeleteExposure(r.exposureId)} style={{ background: 'transparent', border: 'none', color: '#f87171', cursor: 'pointer', fontSize: 12 }}>
                    Eliminar
                  </button>
                </div>
              </div>
            ))}
          </div>

          <div style={{ background: '#0f172a', border: '1px solid #334155', borderRadius: 10, padding: 14 }}>
            <label style={{ fontWeight: 600, display: 'block', marginBottom: 10, color: '#e2e8f0' }}>Agregar agente</label>
            <select style={{ ...inputStyle, marginBottom: 8 }} value={agentId} onChange={e => setAgentId(e.target.value)}>
              <option value="" style={{ color: '#000000' }}>— Seleccionar agente biológico —</option>
              {agents.map(a => <option key={a.id} value={a.id} style={{ color: '#000000' }}>{a.name} (Grupo {a.grupo})</option>)}
            </select>
            <div style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
              <select style={inputStyle} value={viaProbabilidad} onChange={e => setViaProbabilidad(e.target.value as ViaProbabilidad)}>
                <option value="incidencia" style={{ color: '#000000' }}>Vía: incidencia (/100.000 hab.)</option>
                <option value="prevalencia" style={{ color: '#000000' }}>Vía: prevalencia (%)</option>
              </select>
              <input type="number" style={inputStyle} placeholder="Valor" value={valorProbabilidad} onChange={e => setValorProbabilidad(e.target.value)} />
            </div>
            <div style={{ display: 'flex', gap: 8, marginBottom: 10 }}>
              <input type="number" style={inputStyle} placeholder="% frecuencia de exposición" value={frecuenciaPct} onChange={e => setFrecuenciaPct(e.target.value)} />
              <input type="number" style={inputStyle} placeholder="% personal vacunado" value={vacunacionPct} onChange={e => setVacunacionPct(e.target.value)} />
            </div>
            {error && <p style={{ color: '#f87171', fontSize: 12, marginBottom: 8 }}>{error}</p>}
            <button onClick={handleAddExposure} disabled={saving} style={{ background: '#2563eb', color: 'white', border: 'none', borderRadius: 6, padding: '8px 16px', fontSize: 13, cursor: 'pointer' }}>
              {saving ? 'Guardando…' : 'Agregar agente'}
            </button>
          </div>
        </>
      )}
    </div>
  );
}
